/**
 * Pouso de foco na ENTRADA de tela — as duas verdades que o Norigin não dá de
 * graça.
 *
 * ===== O BUG QUE ISTO CONSERTA (medido no board 2.21.0, LG real) =====
 * Voltando de Configurações pra /search, `[data-focused]` ficava NULL: nenhum
 * halo em lugar nenhum, 5 toques de ↓ não moviam nada, e só o Back respondia
 * (ele é listener de window, independe do spatial nav). D-pad morto fere
 * "catálogo 100% navegável".
 *
 * A causa NÃO era foco faltando — era foco MENTIDO. No
 * @noriginmedia/norigin-spatial-navigation@3.1.0:
 *
 *   getNextFocusKey(target)      → "Security check, if component doesn't exist,
 *                                   stay on the same focusKey" → devolve o alvo
 *                                   CRU, mesmo que ninguém tenha registrado ele.
 *   setCurrentFocusedKey(novo)   → BLURA o antigo (`removeAttribute('data-focused')`),
 *                                   grava `this.focusKey = novo`, e só então
 *                                   `if (isFocusableComponent(novo))` — falso pra
 *                                   key inexistente, então NÃO foca nada.
 *
 * Ou seja: `setFocus('key-que-nao-existe')` DESLIGA o foco da tela e ainda faz
 * `getCurrentFocusKey()` devolver exatamente a key pedida. O restore da Busca
 * checava sucesso com `getCurrentFocusKey() === alvo` — TAUTOLOGIA: dava
 * verdadeiro com zero elementos focados, o retry-loop encerrava na 1ª volta e o
 * fallback pro campo virava CÓDIGO MORTO. E aí o `smartNavigate` do Norigin
 * também desiste ("Aborted due to missing current component and force-focusable
 * key" — o app não usa `forceFocus` em lugar nenhum), então as setas param de
 * funcionar de vez.
 *
 * A PROVA REAL de que o foco pousou é o DOM: existe `[data-focused]`. É o mesmo
 * atributo que o resto do app usa como padrão de foco (NUNCA a classe `.focused`).
 *
 * As outras 7 ocorrências da tautologia (HomeScreen ×1, MoviesScreen ×3,
 * SeriesScreen ×3) foram fechadas em `fix/tv-entry-focus-screens`, com o
 * `decideEntryFocus` daqui de baixo.
 */

/**
 * O wrapper `[data-card-key]` desse card, ou null.
 *
 * POR QUE VARREDURA, e não um `querySelector` com template string: o focusKey
 * carrega um id que vem do PROVEDOR (`s-mv-${id}`), e provedor Xtream devolve
 * dado sujo. Uma ASPA DUPLA no id fecha a string no meio do seletor e o
 * `querySelector` LANÇA. O call site é o `onArrowPress` do grid da Busca — o
 * estouro cairia a cada ↑ do D-pad. Comparar por `getAttribute` é imune a isso
 * e barato (o grid tem no máximo algumas dezenas de cards).
 *
 * NOTA HISTÓRICA: aqui viviam também `isCardPresent` e `isFocusLanded`. As duas
 * ficaram SEM call site de produção quando a Busca passou a usar o
 * `startEntryFocus` — e mantê-las seria pior que removê-las, porque `isCardPresent`
 * é justamente o guarda ERRADO do P0 (presença no DOM diverge do registro no
 * Norigin) e `isFocusLanded` convidava a substituir o predicado correto
 * (`screenHasFocus` com `owner`) por um mais fraco. Quem precisa do contrato da
 * biblioteca tem os testes em `norigin-focus-contract.test.ts` e
 * `screens/search-entry-focus.test.ts`.
 */
export function findCardByKey(
  cardKey: string,
  root: Pick<ParentNode, 'querySelectorAll'>,
): Element | null {
  const nodes = root.querySelectorAll('[data-card-key]')
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node?.getAttribute('data-card-key') === cardKey) return node
  }
  return null
}

/**
 * Quem o Norigin acha que tem o foco, e se esse dono ainda existe. Entra por
 * parâmetro (em vez de a lib importar o Norigin) pra `screenHasFocus` seguir
 * puro e testável sem subir a biblioteca.
 */
export interface FocusOwner {
  /** `getCurrentFocusKey()` — a key que o Norigin tem gravada. */
  currentKey: string | null | undefined
  /** `doesFocusableExist` — essa key ainda está registrada? */
  isRegistered: (focusKey: string) => boolean
}

/**
 * O documento tem foco pousado VIVO? É a pergunta que separa "a tela está
 * órfã" (D-pad morto) de "o foco está em outro lugar legítimo".
 *
 * ===== POR QUE O DOM SOZINHO NÃO RESPONDE (achado do review do rc19) =====
 * `querySelector('[data-focused]') !== null` acusa foco onde já não há
 * navegação possível. O atributo pode ficar ÓRFÃO num elemento ainda montado:
 * `removeFocusable` (core dist :1012-1019) faz `delete focusableComponents[key]`
 * e NÃO limpa o `data-focused` do nó; e o blur de `setCurrentFocusedKey` não
 * salva depois, porque ele só roda `if (isFocusableComponent(this.focusKey))`
 * (:1060) — que agora é falso. É o que um grid virtualizado provoca ao
 * desmontar o card FOCADO: o registro some, o atributo fica.
 *
 * Nesse estado o `smartNavigate` aborta ("missing current component"): as setas
 * estão mortas. Mas o predicado antigo dizia "tem foco", então a rede de
 * segurança NÃO entrava — a tela terminava exatamente no estado que esta leva
 * inteira existe pra impedir.
 *
 * O cruzamento é deliberadamente CONSERVADOR: pergunta pelo REGISTRO da key
 * corrente, não pela identidade DOM do elemento focado. Foco legítimo em alvo
 * sem wrapper de card (o campo da Busca, itens de TopNav, chips) tem key
 * registrada e continua contando como pouso — o que evita trocar um bug por
 * outro, disparando a rede em cima de foco bom.
 *
 * Sem `owner` mantém o contrato antigo (só DOM). Quem decide órfã-ou-não DEVE
 * passar o `owner`; a forma de 1 argumento existe pra checagens de conveniência
 * onde só interessa "pintou alguma coisa".
 */
export function screenHasFocus(
  root: Pick<ParentNode, 'querySelector'>,
  owner?: FocusOwner,
): boolean {
  if (root.querySelector('[data-focused]') === null) return false
  if (!owner) return true
  const { currentKey } = owner
  // Sem key gravada o Norigin não tem componente corrente: as setas já morreram,
  // qualquer `[data-focused]` que exista é resto.
  if (!currentKey) return false
  return owner.isRegistered(currentKey)
}

/**
 * Retrato do mundo no instante de uma tentativa de pouso. Tudo o que o
 * `decideEntryFocus` precisa saber — nada de Norigin, nada de DOM, nada de
 * timer. É o que torna a decisão testável de verdade.
 */
export interface EntryFocusSnapshot {
  /** Alvo desejado (pode ainda não ter montado — ou nunca montar). */
  targetKey: string
  /** Alvo SEMPRE montado da tela (na prática o item de TopNav: `nav-*`). */
  safeKey: string
  /** Foco no instante em que o effect começou (`''` se não havia nenhum). */
  initialKey: string
  /** `getCurrentFocusKey()` agora — lembrando que ele MENTE (ver banner). */
  currentKey: string
  /** `doesFocusableExist(targetKey)` — o alvo está registrado no Norigin? */
  targetExists: boolean
  /** `doesFocusableExist(safeKey)`. */
  safeExists: boolean
  /** Existe `[data-focused]` no documento? (`screenHasFocus`) */
  hasFocus: boolean
  /** Tentativas restantes no orçamento. */
  triesLeft: number
  /**
   * Quantos `focus-target` já emitimos SEM que o pouso se confirmasse.
   *
   * Existe porque `targetExists` (registro no Norigin) não garante pouso: a
   * pintura do atributo é `newComponent.node?.setAttribute?.('data-focused')`
   * (core dist :1074) — com o nó nulo, ou já removido do documento, a key é
   * gravada e NADA é pintado. Sem contar as tentativas, a regra (1) devolveria
   * `focus-target` em todo tick e a rede (3) nunca entraria: 20 voltas
   * re-blurando, terminando órfão. Ver `MAX_TARGET_ATTEMPTS`.
   *
   * NÃO REARMA: uma vez esgotado, não voltamos ao alvo nem que ele fique
   * saudável no tick seguinte. É aceitável de propósito — a rede já pousou foco,
   * a tela está navegável, e o alvo preferido não vale um segundo ciclo de
   * blur/re-blur em cima de um foco que já serve. Reentrar na tela recomeça
   * tudo do zero (o contador é local ao loop).
   */
  targetAttempts: number
  /**
   * Ticks já gastos ESPERANDO um alvo que ainda não registrou, com a tela já
   * focada. Bound do `MAX_WAIT_FOR_TARGET`: sem ele, o caminho "alvo que nunca
   * registra" devolvia `retry` do tick 2 ao 20 sem fazer nada — o foco já
   * correto e 20 timers acordando no momento mais caro da entrada em vlow.
   */
  waitAttempts: number
}

/**
 * Quantos `focus-target` sem pouso toleramos antes de cair na rede.
 *
 * NÃO é orçamento de aparelho lento. `targetAttempts` só incrementa em
 * `focus-target`, que já exige o alvo REGISTRADO — e a pintura do
 * `data-focused` acontece SINCRONAMENTE dentro do `setCurrentFocusedKey`
 * (:1074), no mesmo tick. Não há layout assentando entre uma coisa e outra.
 * Device lento atrasa o REGISTRO, e esperar registro queima `triesLeft` (20),
 * não este contador.
 *
 * Então isto é um detector de "alvo registrado que se RECUSA a pintar" — nó
 * nulo, nó fora do documento, desregistro entre a checagem e a chamada. 2 e não
 * 1 porque a corrida desregistrar-no-meio pode acontecer uma vez de forma
 * benigna e re-tentar sai mais barato que o salto safe → alvo; duas seguidas
 * não é corrida, é um alvo que não pousa, e aí a invariante "a tela nunca
 * termina sem foco" ganha da preferência pelo alvo.
 */
export const MAX_TARGET_ATTEMPTS = 2

/**
 * Quantos ticks esperamos um alvo AINDA NÃO REGISTRADO, já com a tela focada.
 *
 * O alvo lazy é caso real e é pra isso que o retry existe: a âncora curada
 * (`curated-*-view-all`) vive num rail que só monta quando os dados pintam.
 * Mas quando ele NUNCA vai registrar — a Busca com `lastSearchFocusKey`
 * persistido de outra busca é o caso do reporte — o loop ficava girando do tick
 * 2 ao 20 com o foco já certo: 20 timers à toa na entrada de tela, que em vlow
 * é o momento mais caro que existe.
 *
 * 5 × 60ms = 300ms: cobre com folga o rail que monta de cache quente (o cenário
 * do BACK, que é quando a âncora curada é setada) e corta ~900ms de cauda
 * ociosa. Bater neste teto não deixa a tela sem foco — a rede já pousou; o que
 * se perde é só a PREFERÊNCIA pelo alvo, e o preço de errar é o foco ficar no
 * TopNav/campo em vez do card.
 */
export const MAX_WAIT_FOR_TARGET = 5

export type EntryFocusAction =
  /** Pousa no alvo desejado — só sai daqui com ele REGISTRADO. */
  | { kind: 'focus-target' }
  /** Rede de segurança: pousa no alvo sempre-montado da tela. */
  | { kind: 'focus-safe' }
  /** Acabou: ou o alvo pousou, ou há foco legítimo que não devemos roubar. */
  | { kind: 'settled' }
  /** Ainda não dá — re-tenta no próximo tick. */
  | { kind: 'retry' }

/**
 * A decisão de UM tick de pouso de foco na entrada/volta de tela.
 *
 * Fecha a MESMA classe de bug da Busca em Home/Movies/Series. As 4 regras que
 * a versão antiga (`setFocus(alvo); if (getCurrentFocusKey() === alvo) return`)
 * violava:
 *
 * 1. NUNCA chamar `setFocus` num alvo não registrado. Essa chamada é
 *    DESTRUTIVA: blura o foco atual, grava a key e não foca nada — a tela fica
 *    órfã e o `smartNavigate` passa a abortar ("missing current component"),
 *    ou seja, as setas morrem. Guardamos com `doesFocusableExist`.
 * 2. A prova de pouso é o DOM (`hasFocus` + key batendo), nunca a key sozinha —
 *    ela é tautologia.
 * 3. INVARIANTE: a tela nunca termina sem foco. Em QUALQUER tick em que o
 *    documento esteja órfão, a rede de segurança pousa no `safeKey` na hora.
 *    É a mesma ordem que ficou certa na Busca — alvo seguro primeiro, alvo
 *    desejado depois. Esperar o alvo desejado antes deixava até ~1,2s de D-pad
 *    morto (20 × 60ms) contra o orçamento de 50ms do ux-tv.
 * 4. Não roubar o foco da mão do usuário: se ele já moveu pra um terceiro
 *    lugar durante o retry, encerramos onde ele está.
 *
 * A ordem das regras importa: a rede de segurança (3) vem depois do
 * `focus-target` (porque com o alvo já registrado o pouso direto é melhor que
 * o salto safe → alvo), mas antes de qualquer `retry` — nunca existe um tick
 * que termine com a tela órfã de propósito.
 *
 * ===== POR QUE A REGRA (1) É LIMITADA POR TENTATIVAS =====
 * Sem o limite, (1) SOMBREIA (3): bastava `targetExists` continuar true e o
 * pouso não acontecer pra todo tick devolver `focus-target` e a rede nunca
 * entrar — 20 voltas re-blurando, terminando órfão. E é alcançável: o Norigin
 * grava `this.focusKey = newFocusKey` incondicionalmente (:1068) mas só pinta
 * `data-focused` com `node?.setAttribute?.()` (:1074), então um alvo registrado
 * com nó nulo — ou desregistrado entre o `doesFocusableExist` e o `setFocus`,
 * que é o que um grid virtualizado faz ao desmontar o card fora da janela —
 * deixa a key gravada e o atributo não. A decisão por TICK estava certa; a
 * invariante por LOOP é que faltava.
 */
export function decideEntryFocus(s: EntryFocusSnapshot): EntryFocusAction {
  // (2) Pouso confirmado pelo DOM: acabou.
  if (s.hasFocus && s.currentKey === s.targetKey) return { kind: 'settled' }

  // (4) O usuário assumiu o controle: há foco de verdade, e não é nem o ponto
  // de partida nem a rede de segurança. Não roubamos.
  if (
    s.hasFocus &&
    s.currentKey !== s.safeKey &&
    s.currentKey !== s.initialKey &&
    s.currentKey !== s.targetKey
  ) {
    return { kind: 'settled' }
  }

  // O alvo já teve as suas chances e não pousou: paramos de insistir nele pra
  // não sombrear a rede (ver o banner acima).
  const targetExhausted = s.targetAttempts >= MAX_TARGET_ATTEMPTS

  // (1) Só chamamos setFocus no alvo depois que ele REGISTROU.
  if (s.targetExists && !targetExhausted) return { kind: 'focus-target' }

  // (3) A tela está órfã: rede de segurança AGORA. Alcançável tanto com o alvo
  // ainda não montado quanto com o alvo esgotado acima.
  // (Guardada também — setFocus no safeKey ausente teria o mesmo efeito
  // destrutivo que estamos consertando.)
  if (!s.hasFocus && s.safeExists) return { kind: 'focus-safe' }

  // ===== FINS HONESTOS DO LOOP (com a invariante já satisfeita) =====
  // Daqui pra baixo, se `hasFocus`, a tela ESTÁ navegável. O que sobra é só a
  // preferência pelo alvo — e continuar girando por ela custa timers na entrada
  // de tela, que em vlow é o pior momento pra gastar.
  if (s.hasFocus) {
    // O alvo está registrado mas se recusa a pintar: não insistimos mais.
    if (targetExhausted) return { kind: 'settled' }
    // O alvo nunca registrou e a janela de espera acabou. Sem isto o caminho
    // "alvo que não vai existir" devolvia `retry` do tick 2 ao 20 sem fazer
    // NADA — 20 timers com o foco já correto.
    if (!s.targetExists && s.waitAttempts >= MAX_WAIT_FOR_TARGET) {
      return { kind: 'settled' }
    }
  }

  // Orçamento acabou: paramos com o que houver (a rede já garantiu foco).
  if (s.triesLeft <= 0) return { kind: 'settled' }

  return { kind: 'retry' }
}
